import React, { useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { addScheduleWeekend } from '../../../api';
import { scheduleApi } from '../../../effector/schedule';
import { Weekend } from '../../../types/marketSchedule';

type Props = {
  id: number;
  weekends: string[];
  onClose: () => void;
};

type FormFields = {
  weekends: string[];
  weekend: Dayjs | null;
};

const today = dayjs().startOf('day');

const validationSchema = Yup.object({
  weekends: Yup.array().of(Yup.string().required()).required(),
  weekend: Yup.mixed<Dayjs>()
    .nullable()
    .required('Укажите дату')
    .test('min-date', 'Дата должна быть не меньше текущей', (value) => !value || !value.isBefore(today, 'day'))
    .test('test-existing', 'Уже присутствует в списке неторговых дней', function validate(weekend) {
      if (!weekend) {
        return true;
      }

      const data = weekend.format('DD.MM.YYYY');
      const weekends = this.parent.weekends as string[];
      return !weekends?.find((day) => day === data);
    }),
});

export const ScheduleAddWeekendDialog: React.FC<Props> = ({ id, weekends, onClose }) => {
  const [send, setSend] = useState(false);
  const [error, setError] = useState<string>();

  const defaultValues = useMemo<FormFields>(
    () => ({
      weekends,
      weekend: null,
    }),
    [weekends],
  );

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<FormFields>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(validationSchema) as never,
  });

  const handleClose = () => {
    if (!send) {
      setError(undefined);
      onClose();
    }
  };

  const onSubmit: SubmitHandler<FormFields> = async (values) => {
    setSend(true);
    setError(undefined);

    const data: Weekend = {
      id,
      day: values.weekend?.format('DD.MM.YYYY') ?? '',
    };

    try {
      const response = await addScheduleWeekend(data);

      if (response?.success) {
        scheduleApi.addWeekend(data);
        onClose();
        return;
      }

      setError(response?.errorMessage ?? 'Ошибка на сервере');
      setSend(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSend(false);
    }
  };

  return (
    <Dialog open onClose={send ? undefined : handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Добавление неторгового дня
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={send}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <Typography>Добавление нерабочего дня или дня со специальным расписанием, предпраздничного дня</Typography>
            <Controller
              name="weekend"
              control={control}
              render={({ field, fieldState }) => (
                <DatePicker
                  label="Дата"
                  value={field.value}
                  onChange={field.onChange}
                  disablePast
                  format="DD.MM.YYYY"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: Boolean(fieldState.error),
                      helperText: fieldState.error?.message,
                    } as Partial<React.ComponentProps<typeof TextField>>,
                  }}
                />
              )}
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={send}>
            Отмена
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isDirty || send}
            startIcon={send ? <CircularProgress size={18} color="inherit" /> : null}
          >
            Сохранить
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
